import type { ReactNode } from "react";
import { Icon } from "../../design-system/primitives/Icon";
import { useLangStore } from "../../store/langStore";
import { t as tr } from "../../lib/i18n";

const AXON_GROUP_URL = "https://axongroup.com.co/";
const AXON_GROUP_LOGO_URL = `${import.meta.env.BASE_URL}axon-group-logo.svg`;

const CARD_DESCRIPTION =
  "En Axon Group Ltda desarrollamos software y proveemos servicios de ingeniería para la automatización de subestaciones y sistemas SCADA del sector eléctrico. Visítanos y conoce más axongroup.com.co.";

function splitToken(
  text: string,
  token: string,
  wrap: (tk: string, key: number) => ReactNode,
): ReactNode[] {
  const parts = text.split(token);
  const out: ReactNode[] = [];
  parts.forEach((p, i) => {
    out.push(p);
    if (i < parts.length - 1) out.push(wrap(token, i));
  });
  return out;
}

/**
 * Tarjeta promocional renderizada en la página de inicio, bajo el panel
 * de modos. Responde "¿qué más hace Axon Group?" con un texto breve y dos
 * sublistas (Productos / Servicios) del catálogo oficial.
 */
export function AxonResourcesCard() {
  const { lang } = useLangStore();
  return (
    <a
      href={AXON_GROUP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="group block w-full max-w-xl rounded-lg border border-[color-mix(in_srgb,var(--color-outline-variant)_40%,transparent)] bg-[color-mix(in_srgb,var(--color-surface-container-low)_40%,transparent)] p-4 transition-colors hover:border-[color-mix(in_srgb,var(--color-brand)_40%,transparent)] hover:bg-[var(--color-surface-container-low)] focus:outline-none focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-brand)_40%,transparent)]"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <img
            src={AXON_GROUP_LOGO_URL}
            alt="Axon Group"
            className="h-7 w-auto select-none opacity-90 transition-opacity group-hover:opacity-100"
            draggable={false}
          />
          <Icon
            name="open_in_new"
            size={14}
            className="flex-shrink-0 text-[var(--color-on-surface-variant)] transition-colors group-hover:text-[var(--color-brand)]"
          />
        </div>
        <div className="flex min-w-0 flex-col gap-2">
          <p className="text-[12px] leading-[1.0625rem] text-[var(--color-on-surface-variant)]">
            {splitToken(tr(CARD_DESCRIPTION, lang), "axongroup.com.co", (tk, k) => (
              <span key={k} className="text-[var(--color-brand)] group-hover:underline">
                {tk}
              </span>
            ))}
          </p>
          <ResourceSection title={tr("Productos", lang)}>
            <ResourceItem name={tr("Axon Test", lang)}>
              {tr("simulador DNP3, IEC 60870-5-101/103/104, Modbus y OPC", lang)}
            </ResourceItem>
            <ResourceItem name={tr("AT61 Simulador", lang)}>
              {tr("IEC 61850 MMS y GOOSE, cliente y servidor", lang)}
            </ResourceItem>
            <ResourceItem name={tr("Axon Builder 3", lang)}>
              {tr("SCADA / IHM para sector eléctrico", lang)}
            </ResourceItem>
            <ResourceItem name={tr("Gateway Axon Exchange", lang)}>
              {tr("conversión entre protocolos de telecontrol", lang)}
            </ResourceItem>
          </ResourceSection>
          <ResourceSection title={tr("Servicios", lang)}>
            <ResourceItem>{tr("Automatización y puesta en servicio de subestaciones", lang)}</ResourceItem>
            <ResourceItem>{tr("Subestaciones digitales — diseño, pruebas y entrenamiento", lang)}</ResourceItem>
            <ResourceItem>{tr("Ciberseguridad OT — NERC CIP, CNO 1347, IEC 62443, ISO 27001", lang)}</ResourceItem>
          </ResourceSection>
        </div>
      </div>
    </a>
  );
}

function ResourceSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wider text-[var(--color-on-surface-variant)]">
        {title}
      </span>
      <ul className="flex flex-col gap-0.5 font-mono text-[10px] text-[color-mix(in_srgb,var(--color-on-surface-variant)_80%,transparent)]">
        {children}
      </ul>
    </div>
  );
}

function ResourceItem({ name, children }: { name?: string; children: ReactNode }) {
  return (
    <li className="flex items-start gap-1.5">
      <Icon
        name="arrow_right"
        size={12}
        className="mt-px flex-shrink-0 text-[color-mix(in_srgb,var(--color-brand)_70%,transparent)]"
      />
      <span className="min-w-0 flex-1">
        {name !== undefined ? (
          <>
            <span className="text-[var(--color-on-surface-variant)]">{name}</span>
            {" — "}
          </>
        ) : null}
        {children}
      </span>
    </li>
  );
}
